type PublicConfig = {
  apiBaseUrl: string;
  guestWebBaseUrl: string;
  scopedWebPath: string;
  eventsPath: string;
  feedbackPath: string;
  sessionPaymentsPath: string;
  sessionBookingsPath: string;
  termsUrl: string;
  privacyUrl: string;
  appStoreUrl: string;
  playStoreUrl: string;
  parentAppLinkBaseUrl: string;
  parentConnectionsPath: string;
  /**
   * Whether the coach workspace is served.
   *
   * The v0 workspace reaches Test ahead of production, so the routes it owns — `/`, `/home` and
   * `/coach` — exist only where this is on. Everywhere else `/` leads to bookings and payments,
   * which is the whole signed-in app without it. Off unless a deployment opts in, so a new target
   * does not serve the workspace by default.
   */
  coachWorkspaceEnabled: boolean;
  /**
   * Origin of the Feature Flag Service, whose public snapshot carries client-visible release flags
   * such as `trainer_profile_cart`. Empty means every runtime flag reads as off.
   */
  featureFlagsBaseUrl: string;
  /**
   * Whether the athlete web experience is served (PLA-1655): web sign-up, `/join`, and the athlete
   * area at `/home`. Off unless a deployment opts in, so production keeps today's behaviour until
   * release.
   */
  athleteWebEnabled: boolean;
  /** Messaging service origin for the athlete inbox; required only when the athlete web is on. */
  messagingApiBaseUrl: string;
};

function required(value: string | undefined, name: string): string {
  if (!value || value.trim() === '') {
    throw new Error(`Missing required env value ${name}`);
  }
  return value.trim();
}

function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/, '');
}

function normalizePath(value: string): string {
  const trimmed = value.trim();
  if (!trimmed.startsWith('/')) {
    return `/${trimmed}`.replace(/\/+$/, '');
  }
  return trimmed.replace(/\/+$/, '');
}

export const publicConfig: PublicConfig = {
  apiBaseUrl: normalizeBaseUrl(required(import.meta.env.VITE_API_BASE_URL, 'VITE_API_BASE_URL')),
  guestWebBaseUrl: normalizeBaseUrl(
    required(import.meta.env.VITE_GUEST_WEB_BASE_URL, 'VITE_GUEST_WEB_BASE_URL'),
  ),
  scopedWebPath: normalizePath(
    required(import.meta.env.VITE_SCOPED_WEB_PATH, 'VITE_SCOPED_WEB_PATH'),
  ),
  eventsPath: normalizePath(required(import.meta.env.VITE_EVENTS_PATH, 'VITE_EVENTS_PATH')),
  feedbackPath: normalizePath(required(import.meta.env.VITE_FEEDBACK_PATH, 'VITE_FEEDBACK_PATH')),
  sessionPaymentsPath: normalizePath(
    import.meta.env.VITE_SESSION_PAYMENTS_PATH ?? '/session-payments',
  ),
  sessionBookingsPath: normalizePath(
    import.meta.env.VITE_SESSION_BOOKINGS_PATH ?? '/session-bookings',
  ),
  termsUrl: required(import.meta.env.VITE_TERMS_URL, 'VITE_TERMS_URL'),
  privacyUrl: required(import.meta.env.VITE_PRIVACY_URL, 'VITE_PRIVACY_URL'),
  appStoreUrl: required(import.meta.env.VITE_APP_STORE_URL, 'VITE_APP_STORE_URL'),
  playStoreUrl: import.meta.env.VITE_PLAY_STORE_URL?.trim() ?? '',
  parentAppLinkBaseUrl:
    import.meta.env.VITE_PARENT_APP_LINK_BASE_URL?.trim() || 'https://78tql.app.link/',
  parentConnectionsPath: normalizePath(
    import.meta.env.VITE_PARENT_CONNECTIONS_PATH || '/parent-connections',
  ),
  coachWorkspaceEnabled: import.meta.env.VITE_COACH_WORKSPACE_ENABLED?.trim() === 'true',
  featureFlagsBaseUrl: normalizeBaseUrl(import.meta.env.VITE_FEATURE_FLAGS_BASE_URL?.trim() ?? ''),
  athleteWebEnabled: import.meta.env.VITE_ATHLETE_WEB_ENABLED?.trim() === 'true',
  messagingApiBaseUrl: normalizeBaseUrl(import.meta.env.VITE_MESSAGING_API_BASE_URL?.trim() ?? ''),
};

if (publicConfig.athleteWebEnabled && !publicConfig.messagingApiBaseUrl) {
  throw new Error('Missing required env value VITE_MESSAGING_API_BASE_URL');
}
